import BaseModal from 'components/Modals/BaseModal';
import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import type { Workplace } from '../types/Deployments.types';

interface RemoveWorkplaceModalProps {
  show: boolean;
  workplace: Workplace | null;
  onClose: () => void;
  /** Resolves on a successful DELETE; rejects with the server error to surface inline. */
  onConfirm: (wpIndex: number, reason: string) => Promise<void>;
}

/**
 * Typed-name confirmation modal for removing a workplace (FE-SPEC Phase 4).
 *
 * Mirrors the GitHub repo-deletion pattern: the user must type the
 * workplace's prefix exactly before the Remove button activates. Prevents
 * fat-fingering a production workplace. Optional reason is logged to command
 * history server-side.
 */
export default function RemoveWorkplaceModal({
  show,
  workplace,
  onClose,
  onConfirm,
}: RemoveWorkplaceModalProps) {
  const [typedPrefix, setTypedPrefix] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  useEffect(() => {
    if (show) {
      setTypedPrefix('');
      setReason('');
      setSubmitError(null);
      setSubmitting(false);
      setConflict(false);
    }
  }, [show]);

  if (!workplace) return null;

  const matches = typedPrefix.trim() === workplace.prefix;
  const reasonProvided = reason.trim().length > 0;
  const canSubmit = matches && reasonProvided && !submitting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    setConflict(false);
    try {
      await onConfirm(workplace.index, reason.trim());
      onClose();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const status = (err as any)?.response?.status;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const serverMsg = (err as any)?.response?.data?.message;
      if (status === 409) {
        // Parent has refetched; keep the modal open so the operator can
        // re-confirm with the bumped lockVersion.
        setConflict(true);
        setSubmitError(
          serverMsg ||
            'Someone else just updated this workplace — review and re-confirm to retry the remove.',
        );
      } else {
        setSubmitError(
          serverMsg || (err instanceof Error ? err.message : 'Failed to remove workplace'),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseModal
      show={show}
      handleShow={onClose}
      size="md"
      title={`Remove workplace — ${workplace.name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirm} disabled={!canSubmit}>
            {submitting ? 'Removing…' : 'Remove workplace'}
          </Button>
        </>
      }
    >
      <div className="text-theme">
        <p className="mb-2">
          This will queue removal of workplace <span className="fw-semibold">{workplace.name}</span>{' '}
          (workplace_id {workplace.id}, prefix {workplace.prefix}). The supervisor must be restarted
          to actually unship its PM2 processes.
        </p>
        <p className="text-theme-muted small mb-3">
          This action is restart-required and can be reversed by adding the workplace back with the
          same prefix.
        </p>
        <Form>
          <Form.Group controlId="rwp-typed-prefix" className="mb-3">
            <Form.Label className="text-theme">
              Type the workplace prefix{' '}
              <span className="font-monospace text-warning">{workplace.prefix}</span> to confirm
            </Form.Label>
            <Form.Control
              value={typedPrefix}
              onChange={(e) => setTypedPrefix(e.target.value)}
              isInvalid={typedPrefix.length > 0 && !matches}
              autoFocus
              placeholder={workplace.prefix}
            />
            <Form.Control.Feedback type="invalid">Prefix doesn’t match.</Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="rwp-reason">
            <Form.Label className="text-theme">Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              isInvalid={reason.length > 0 && !reasonProvided}
              placeholder="Decommissioning the QA deployment"
            />
            <Form.Control.Feedback type="invalid">Required for audit trail.</Form.Control.Feedback>
            <Form.Text className="text-theme-muted">
              Required — logged to command history so the removal has a paper trail.
            </Form.Text>
          </Form.Group>
        </Form>
        {submitError && (
          <div className={`mt-3 ${conflict ? 'text-warning' : 'text-danger'}`}>
            {conflict ? 'Conflict' : 'Remove failed'} — {submitError}
          </div>
        )}
      </div>
    </BaseModal>
  );
}
